import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, FileText, AlertTriangle, BarChart3, RefreshCw, DollarSign, Ship, Droplets, Wind, Activity, Mountain, Sun, Bug, CheckCircle, Anchor } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Radar, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import api from '@/lib/api';
import { getRiskColor, getRiskLevel, getRiskBadgeClass, formatDate, formatDateTime, RISK_CATEGORIES_LABELS } from '@/lib/utils';
import { NavBar } from '@/components/NavBar';

ChartJS.register(RadialLinearScale, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Tooltip, Legend);

const CountryDetailPage = () => {
  const { countryCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState(null);
  const [signals, setSignals] = useState([]);
  const [absData, setAbsData] = useState(null);
  const [iloData, setIloData] = useState(null);
  const [oecData, setOecData] = useState(null);
  const [portData, setPortData] = useState(null);
  const [sentimentData, setSentimentData] = useState(null);
  const [mediaVolumeData, setMediaVolumeData] = useState(null);
  const [econIndicators, setEconIndicators] = useState(null);
  const [tradeProducts, setTradeProducts] = useState(null);

  const fetchCountryData = useCallback(async () => {
    try {
      const [countryRes, signalsRes] = await Promise.all([
        api.getCountryDetail(countryCode),
        api.getCountrySignals(countryCode, 20)
      ]);
      setCountry(countryRes.data);
      setSignals(signalsRes.data);
      // Load new data sources non-blocking
      Promise.all([
        api.getABSTrade(countryCode).catch(() => ({ data: null })),
        api.getILOLabour(countryCode).catch(() => ({ data: null })),
        api.getOECComplexity(countryCode).catch(() => ({ data: null })),
        api.getCountryPorts(countryCode).catch(() => ({ data: { ports: [] } })),
        api.getSentimentTimeline(countryCode).catch(() => ({ data: null })),
        api.getMediaVolume(countryCode).catch(() => ({ data: null })),
        api.getEconomicIndicators(countryCode).catch(() => ({ data: null })),
        api.getTradeProducts(countryCode).catch(() => ({ data: null })),
      ]).then(([absRes, iloRes, oecRes, portRes, sentRes, mediaRes, econRes, tradeRes]) => {
        setAbsData(absRes.data);
        setIloData(iloRes.data);
        setOecData(oecRes.data);
        setPortData(portRes.data);
        setSentimentData(sentRes.data);
        setMediaVolumeData(mediaRes.data);
        setEconIndicators(econRes.data);
        setTradeProducts(tradeRes.data);
      });
    } catch (error) {
      console.error('Error fetching country data:', error);
    } finally {
      setLoading(false);
    }
  }, [countryCode]);

  useEffect(() => {
    fetchCountryData();
  }, [fetchCountryData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="animate-pulse text-[#4E6974]">Loading country data...</div>
      </div>
    );
  }

  if (!country) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#4E6974] mb-4">Country not found</p>
          <Button onClick={() => navigate('/dashboard')} variant="outline" data-testid="return-dashboard-btn">
            Return to dashboard
          </Button>
        </div>
      </div>
    );
  }

  const tradeData = country.trade_data || {};
  const monthlyImports = tradeData.monthly_imports || [];
  const monthlyExports = tradeData.monthly_exports || [];
  const hasMonthlyData = monthlyImports.length > 0 || monthlyExports.length > 0;
  const isComtradeData = tradeData.data_source === 'comtrade';

  // Radar chart config
  const radarData = {
    labels: Object.keys(country.risk_breakdown || {}).map(key =>
      RISK_CATEGORIES_LABELS[key] || key.replace(/_/g, ' ')
    ),
    datasets: [{
      label: 'Risk Score',
      data: Object.values(country.risk_breakdown || {}),
      backgroundColor: 'rgba(0, 151, 167, 0.2)',
      borderColor: '#0097A7',
      borderWidth: 2,
      pointBackgroundColor: '#0097A7',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
    }]
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        min: 0,
        max: 100,
        ticks: { stepSize: 20, color: '#4E6974', font: { family: 'DM Sans', size: 10 } },
        grid: { color: 'rgba(0,0,0,0.05)' },
        pointLabels: { color: '#0D3B4F', font: { family: 'DM Sans', size: 11, weight: 500 } }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: { backgroundColor: '#0A2533', titleColor: '#fff', bodyColor: '#fff', padding: 12, cornerRadius: 8 }
    }
  };

  // Monthly trade bar chart config
  const allMonths = [...new Set([
    ...monthlyImports.map(m => m.period_display),
    ...monthlyExports.map(m => m.period_display)
  ])].sort();

  const importMap = Object.fromEntries(monthlyImports.map(m => [m.period_display, m.value_usd]));
  const exportMap = Object.fromEntries(monthlyExports.map(m => [m.period_display, m.value_usd]));

  const monthlyChartData = {
    labels: allMonths,
    datasets: [
      {
        label: 'Imports from AU (USD)',
        data: allMonths.map(m => (importMap[m] || 0) / 1e6),
        backgroundColor: 'rgba(0, 151, 167, 0.7)',
        borderColor: '#0097A7',
        borderWidth: 1,
        borderRadius: 3,
      },
      {
        label: 'Exports to AU (USD)',
        data: allMonths.map(m => (exportMap[m] || 0) / 1e6),
        backgroundColor: 'rgba(77, 208, 225, 0.7)',
        borderColor: '#4DD0E1',
        borderWidth: 1,
        borderRadius: 3,
      }
    ]
  };

  const monthlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#4E6974', font: { family: 'DM Sans', size: 10 }, maxRotation: 45, minRotation: 45 }
      },
      y: {
        grid: { color: 'rgba(0,0,0,0.04)' },
        ticks: {
          color: '#4E6974',
          font: { family: 'DM Sans', size: 10 },
          callback: (val) => `$${val.toFixed(0)}M`
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
        labels: { color: '#0D3B4F', font: { family: 'DM Sans', size: 11 }, usePointStyle: true, pointStyle: 'rect' }
      },
      tooltip: {
        backgroundColor: '#0A2533',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: $${ctx.raw.toFixed(1)}M`
        }
      }
    }
  };

  const formatBillions = (val) => {
    if (!val || val === 0) return 'N/A';
    if (val >= 1e9) return `$${(val / 1e9).toFixed(1)}B`;
    if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
    return `$${val.toLocaleString()}`;
  };

  const ChangeIndicator = ({ value, label, testId }) => {
    const isPositive = value > 0;
    const isZero = value === 0 || value === undefined || value === null;
    return (
      <div className="text-center" data-testid={testId}>
        <p className="text-xs text-[#4E6974] mb-1">{label}</p>
        <div className={`text-xl font-bold ${isZero ? 'text-[#4E6974]' : isPositive ? 'text-[#2E7D32]' : 'text-[#FF3B30]'}`}>
          {isZero ? '0.0%' : `${isPositive ? '+' : ''}${value?.toFixed(1)}%`}
        </div>
      </div>
    );
  };

  const TrendIcon = ({ trend }) => {
    switch (trend?.toLowerCase()) {
      case 'up': return <TrendingUp className="h-4 w-4 text-[#FF3B30]" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-[#2E7D32]" />;
      default: return <Minus className="h-4 w-4 text-[#4E6974]" />;
    }
  };

  const disruptions = tradeData.disruption_signals || [];

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Navigation */}
      <NavBar />

      <main className="pt-24 px-4 lg:px-6 pb-8">
        <div className="max-w-[1400px] mx-auto">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/countries')}
            className="mt-6 text-[#4E6974] hover:text-[#0D3B4F]"
            data-testid="back-to-countries-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to countries
          </Button>

          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-4 mb-8">
            <div className="flex items-center gap-4 mb-4">
              <span className="text-5xl">{country.flag_emoji}</span>
              <div>
                <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="country-name">
                  {country.country_name}
                </h1>
                <p className="text-[#4E6974] text-sm">
                  Last updated: {formatDateTime(country.last_updated)}
                </p>
              </div>
              <div className="ml-auto flex items-center gap-4">
                <div className="text-right">
                  <div className="flex items-center gap-2">
                    <span className="text-4xl font-bold" style={{ color: getRiskColor(country.overall_risk_score) }} data-testid="country-risk-score">
                      {country.overall_risk_score}
                    </span>
                    <span className="text-2xl text-[#4E6974]">/100</span>
                    <TrendIcon trend={country.risk_trend} />
                  </div>
                  <Badge className={`${getRiskBadgeClass(getRiskLevel(country.overall_risk_score))} mt-1`}>
                    {getRiskLevel(country.overall_risk_score)} RISK
                  </Badge>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Risk Breakdown Radar */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-[400px]">
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-[#0D3B4F]">Risk score breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <Radar data={radarData} options={radarOptions} />
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* AI Risk Narrative */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-2">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-[400px]">
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-[#0D3B4F]" data-testid="ai-narrative-title">Current risk assessment</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px] pr-4">
                    <div className="prose prose-sm max-w-none text-[#4E6974] leading-relaxed">
                      {country.ai_narrative?.split('\n\n').map((paragraph, i) => (
                        <p key={i} className="mb-4" data-testid={`narrative-paragraph-${i}`}>
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Currency, Disaster, Freight Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            {/* Currency */}
            {country.currency_data && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}>
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-full" data-testid="country-currency-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-bold text-[#0D3B4F] flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-[#0097A7]" /> Exchange rate
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#4E6974] mb-3">AUD / {country.currency_data.currency_code}</p>
                    <div className="flex items-end gap-2 mb-2">
                      <span className="text-3xl font-bold text-[#0D3B4F]">
                        {country.currency_data.aud_rate < 100 ? country.currency_data.aud_rate.toFixed(4) : country.currency_data.aud_rate.toFixed(1)}
                      </span>
                      <span className={`text-sm font-medium mb-1 ${country.currency_data.change_30d_pct > 0 ? 'text-[#FF3B30]' : country.currency_data.change_30d_pct < 0 ? 'text-[#2E7D32]' : 'text-[#4E6974]'}`}>
                        {country.currency_data.change_30d_pct > 0 ? '+' : ''}{country.currency_data.change_30d_pct?.toFixed(1)}% 30d
                      </span>
                    </div>
                    <p className="text-xs text-[#4E6974] leading-relaxed">{country.currency_data.narrative}</p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Active Hazards */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.23 }}>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-full" data-testid="country-disasters-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-bold text-[#0D3B4F] flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-[#FF9500]" /> Active hazards
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {country.disaster_data?.has_active ? (
                    <div className="space-y-2">
                      {country.disaster_data.disasters.slice(0, 3).map((d, i) => (
                        <DisasterCard key={d.id || i} disaster={d} />
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-[#F0FFF4]">
                      <CheckCircle className="h-4 w-4 text-[#2E7D32]" />
                      <span className="text-sm text-[#2E7D32] font-medium">No active natural hazard alerts for {country.country_name}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Freight */}
            {country.freight_data && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }}>
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-full" data-testid="country-freight-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-bold text-[#0D3B4F] flex items-center gap-2">
                      <Ship className="h-4 w-4 text-[#0097A7]" /> Freight rate
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#4E6974] mb-3">{country.freight_data.origin} → {country.freight_data.destination}</p>
                    <div className="flex items-end gap-2 mb-2">
                      <span className="text-3xl font-bold text-[#0D3B4F]">${country.freight_data.current_rate_usd?.toLocaleString()}</span>
                      <span className="text-sm text-[#4E6974] mb-1">/FEU</span>
                      <span className={`text-sm font-medium mb-1 ${country.freight_data.wow_change_pct > 0 ? 'text-[#FF3B30]' : 'text-[#2E7D32]'}`}>
                        WoW: {country.freight_data.wow_change_pct > 0 ? '+' : ''}{country.freight_data.wow_change_pct?.toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-xs text-[#4E6974] leading-relaxed">{country.freight_data.narrative}</p>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          {/* Trade Disruption Alerts */}
          {disruptions.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="mt-6">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none border-l-4 border-l-[#FF3B30]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-bold text-[#0D3B4F] flex items-center gap-2" data-testid="disruption-alerts-title">
                    <AlertTriangle className="h-5 w-5 text-[#FF3B30]" />
                    Trade disruption alerts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {disruptions.map((signal, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-[#FFF3F0]" data-testid={`disruption-signal-${idx}`}>
                        <Badge className={signal.severity === 'HIGH' ? 'bg-[#FF3B30] text-white' : 'bg-[#FF9500] text-white'}>
                          {signal.severity}
                        </Badge>
                        <span className="text-sm text-[#0D3B4F] font-medium">{signal.message}</span>
                        <span className="ml-auto text-sm font-bold text-[#FF3B30]">{signal.change_pct?.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Monthly Trade Trends Chart */}
          {hasMonthlyData && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-6">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold text-[#0D3B4F] flex items-center gap-2" data-testid="monthly-trade-chart-title">
                      <BarChart3 className="h-5 w-5 text-[#0097A7]" />
                      Monthly bilateral trade trends (last 12 months)
                    </CardTitle>
                    <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7]">UN Comtrade</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* MoM Change Indicators */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 rounded-lg bg-[#F5F5F5]">
                    <ChangeIndicator value={tradeData.import_mom_change_pct} label="Import MoM change" testId="import-mom-change" />
                    <ChangeIndicator value={tradeData.export_mom_change_pct} label="Export MoM change" testId="export-mom-change" />
                    <div className="text-center" data-testid="monthly-import-avg">
                      <p className="text-xs text-[#4E6974] mb-1">Monthly import avg</p>
                      <div className="text-xl font-bold text-[#0D3B4F]">{formatBillions(tradeData.monthly_import_avg_usd)}</div>
                    </div>
                    <div className="text-center" data-testid="monthly-export-avg">
                      <p className="text-xs text-[#4E6974] mb-1">Monthly export avg</p>
                      <div className="text-xl font-bold text-[#0D3B4F]">{formatBillions(tradeData.monthly_export_avg_usd)}</div>
                    </div>
                  </div>
                  {/* Bar chart */}
                  <div className="h-[300px]" data-testid="monthly-trade-chart">
                    <Bar data={monthlyChartData} options={monthlyChartOptions} />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Annual Trade Data + Signals */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="mt-6">
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-bold text-[#0D3B4F]" data-testid="trade-exposure-title">Trade exposure data</CardTitle>
                  {isComtradeData && <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7]">Live data</Badge>}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Annual totals (only for comtrade data) */}
                  {isComtradeData && (
                    <>
                      <div data-testid="annual-imports">
                        <h4 className="text-sm font-semibold text-[#0D3B4F] mb-3">Annual imports (2024)</h4>
                        <div className="text-2xl font-bold text-[#0D3B4F]">{formatBillions(tradeData.total_imports_usd)}</div>
                        <ChangeIndicator value={tradeData.import_yoy_change_pct} label="Year-over-year" testId="import-yoy-change" />
                      </div>
                      <div data-testid="annual-exports">
                        <h4 className="text-sm font-semibold text-[#0D3B4F] mb-3">Annual exports (2024)</h4>
                        <div className="text-2xl font-bold text-[#0D3B4F]">{formatBillions(tradeData.total_exports_usd)}</div>
                        <ChangeIndicator value={tradeData.export_yoy_change_pct} label="Year-over-year" testId="export-yoy-change" />
                      </div>
                    </>
                  )}
                  {!isComtradeData && (
                    <>
                      <div>
                        <h4 className="text-sm font-semibold text-[#0D3B4F] mb-3">Top imports from Australia</h4>
                        <ul className="space-y-2">
                          {tradeData.top_imports_from_au?.map((item, i) => (
                            <li key={i} className="text-sm text-[#4E6974] flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#00707C]" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-[#0D3B4F] mb-3">Top exports to Australia</h4>
                        <ul className="space-y-2">
                          {tradeData.top_exports_to_au?.map((item, i) => (
                            <li key={i} className="text-sm text-[#4E6974] flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#4DD0E1]" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}
                  <div data-testid="trade-yoy">
                    <h4 className="text-sm font-semibold text-[#0D3B4F] mb-3">Trade volume change (YoY)</h4>
                    <div className={`text-3xl font-bold ${tradeData.trade_volume_yoy >= 0 ? 'text-[#2E7D32]' : 'text-[#FF3B30]'}`}>
                      {tradeData.trade_volume_yoy >= 0 ? '+' : ''}{tradeData.trade_volume_yoy?.toFixed(1) || '0.0'}%
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-[#0D3B4F] mb-3">Trade agreements</h4>
                    <div className="flex flex-wrap gap-2">
                      {tradeData.trade_agreements?.map((agreement, i) => (
                        <Badge key={i} variant="secondary" className="bg-[#F5F5F5] text-[#4E6974]">
                          {agreement}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Commodity lists for comtrade data too */}
                {isComtradeData && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-[#E0E0E0]">
                    <div>
                      <h4 className="text-sm font-semibold text-[#0D3B4F] mb-3">Top imports from Australia</h4>
                      <ul className="space-y-2">
                        {tradeData.top_imports_from_au?.map((item, i) => (
                          <li key={i} className="text-sm text-[#4E6974] flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00707C]" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#0D3B4F] mb-3">Top exports to Australia</h4>
                      <ul className="space-y-2">
                        {tradeData.top_exports_to_au?.map((item, i) => (
                          <li key={i} className="text-sm text-[#4E6974] flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#4DD0E1]" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Signals Timeline */}
          {/* New Data Sources: ABS Trade + ILO Labour + OEC + Port Status */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* ABS Trade Data */}
            {absData && absData.status !== 'unavailable' && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.41 }}>
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-full" data-testid="abs-trade-section">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold text-[#0D3B4F]">Australia's trade with {country.country_name}</CardTitle>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">ABS</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="p-3 rounded-lg bg-[#F5F5F5]">
                        <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">AU Imports</p>
                        <p className="text-lg font-bold text-[#0D3B4F]">{absData.total_imports_aud > 0 ? `$${(absData.total_imports_aud / 1e9).toFixed(1)}B` : 'N/A'}</p>
                        {absData.import_yoy_pct !== 0 && <p className={`text-xs ${absData.import_yoy_pct > 0 ? 'text-[#2E7D32]' : 'text-[#FF3B30]'}`}>{absData.import_yoy_pct > 0 ? '+' : ''}{absData.import_yoy_pct}% YoY</p>}
                      </div>
                      <div className="p-3 rounded-lg bg-[#F5F5F5]">
                        <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">AU Exports</p>
                        <p className="text-lg font-bold text-[#0D3B4F]">{absData.total_exports_aud > 0 ? `$${(absData.total_exports_aud / 1e9).toFixed(1)}B` : 'N/A'}</p>
                        {absData.export_yoy_pct !== 0 && <p className={`text-xs ${absData.export_yoy_pct > 0 ? 'text-[#2E7D32]' : 'text-[#FF3B30]'}`}>{absData.export_yoy_pct > 0 ? '+' : ''}{absData.export_yoy_pct}% YoY</p>}
                      </div>
                      <div className="p-3 rounded-lg bg-[#F5F5F5]">
                        <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Trade balance</p>
                        <p className={`text-lg font-bold ${absData.trade_balance_aud >= 0 ? 'text-[#2E7D32]' : 'text-[#FF3B30]'}`}>{absData.trade_balance_aud > 0 ? `+$${(absData.trade_balance_aud / 1e9).toFixed(1)}B` : absData.trade_balance_aud < 0 ? `-$${(Math.abs(absData.trade_balance_aud) / 1e9).toFixed(1)}B` : '$0'}</p>
                      </div>
                    </div>
                    {absData.latest_period && <p className="text-xs text-[#4E6974] italic">Latest period: {absData.latest_period}. Source: Australian Bureau of Statistics</p>}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* ILO Labour Market */}
            {iloData && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.43 }}>
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-full" data-testid="ilo-labour-section">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold text-[#0D3B4F]">Labour market and workforce</CardTitle>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">ILO</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      <div className="p-3 rounded-lg bg-[#F5F5F5]">
                        <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Mfg wage</p>
                        <p className="text-base font-bold text-[#0D3B4F]">${iloData.manufacturing_wage_aud?.toLocaleString()}</p>
                        <p className="text-[10px] text-[#4E6974]">AUD/month</p>
                      </div>
                      <div className="p-3 rounded-lg bg-[#F5F5F5]">
                        <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Wage growth</p>
                        <p className={`text-base font-bold ${iloData.wage_growth_pct > 10 ? 'text-[#FF9500]' : 'text-[#0D3B4F]'}`}>{iloData.wage_growth_pct}%</p>
                        <p className="text-[10px] text-[#4E6974]">annual</p>
                      </div>
                      <div className="p-3 rounded-lg bg-[#F5F5F5]">
                        <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Unemployment</p>
                        <p className="text-base font-bold text-[#0D3B4F]">{iloData.unemployment_pct}%</p>
                      </div>
                      <div className="p-3 rounded-lg bg-[#F5F5F5]">
                        <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Safety</p>
                        <p className={`text-base font-bold ${iloData.safety_rating === 'poor' ? 'text-[#FF3B30]' : iloData.safety_rating === 'excellent' ? 'text-[#2E7D32]' : 'text-[#0D3B4F]'}`}>{iloData.safety_rating}</p>
                      </div>
                    </div>
                    {iloData.strike_days > 15 && <p className="text-xs text-[#FF9500] font-medium mb-2">Elevated industrial action: {iloData.strike_days} days lost to strikes in {country.country_name}</p>}
                    {iloData.risk_factors?.length > 0 && (
                      <div className="space-y-1 mt-2">
                        {iloData.risk_factors.map((f, i) => <p key={i} className="text-xs text-[#4E6974]">- {f}</p>)}
                      </div>
                    )}
                    <p className="text-[10px] text-[#4E6974] mt-3 italic">Source: International Labour Organisation (ILOSTAT)</p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* OEC Economic Complexity */}
            {oecData && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-full" data-testid="oec-complexity-section">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold text-[#0D3B4F]">Economic complexity and trade structure</CardTitle>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">OEC</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="p-3 rounded-lg bg-[#F5F5F5]">
                        <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">ECI Score</p>
                        <p className="text-lg font-bold text-[#0D3B4F]">{oecData.eci?.toFixed(2)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-[#F5F5F5]">
                        <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Global rank</p>
                        <p className="text-lg font-bold text-[#0D3B4F]">#{oecData.eci_rank}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-[#F5F5F5]">
                        <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Diversification</p>
                        <p className="text-sm font-bold text-[#0D3B4F] capitalize">{oecData.diversification}</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#4E6974]">
                      {country.country_name} has an economic complexity index of {oecData.eci?.toFixed(2)}, ranking #{oecData.eci_rank} globally. Its economy is {oecData.diversification} with the top sector accounting for {oecData.top_sector_share_pct}% of total exports.
                    </p>
                    <p className="text-[10px] text-[#4E6974] mt-3 italic">Source: Observatory of Economic Complexity</p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Port Status */}
            {portData?.ports?.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.47 }}>
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-full" data-testid="port-status-section">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold text-[#0D3B4F]">Shipping and logistics</CardTitle>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">Ports</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {portData.ports.map((port) => {
                        const lc = port.congestion_level === 'Severe' ? '#FF3B30' : port.congestion_level === 'High' ? '#FF9500' : port.congestion_level === 'Moderate' ? '#FFCC02' : '#2E7D32';
                        return (
                          <div key={port.port_id} className="p-3 rounded-lg bg-[#F5F5F5]">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: lc }} />
                                <span className="text-sm font-semibold text-[#0D3B4F]">{port.port_name}</span>
                              </div>
                              <Badge className="text-xs" style={{ backgroundColor: `${lc}20`, color: lc }}>{port.congestion_level}</Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs text-[#4E6974] mt-2">
                              <span>Wait: {port.wait_days}d</span>
                              <span>Transit to AU: {port.transit_days_to_au}d</span>
                              <span>CPPI rank: #{port.cppi_rank}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {portData.ports.some(p => p.congestion_level === 'High' || p.congestion_level === 'Severe') && (
                      <p className="text-xs text-[#FF9500] mt-3 font-medium">Port congestion at {portData.ports.filter(p => p.congestion_level === 'High' || p.congestion_level === 'Severe').map(p => p.port_name).join(', ')} is adding estimated delays to transit times.</p>
                    )}
                    <p className="text-[10px] text-[#4E6974] mt-3 italic">Source: World Bank CPPI + freight rate model</p>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          {/* Signals Timeline (original) */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-6">
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-[#0D3B4F]">Key signals</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[350px]">
                  <div className="space-y-3">
                    {signals.map((signal, index) => (
                      <div
                        key={signal.id}
                        className="flex gap-4 p-4 rounded-lg bg-[#F5F5F5] hover:bg-[#EEEEEE] transition-colors"
                        data-testid={`signal-item-${index}`}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <Badge className={`${getRiskBadgeClass(signal.severity)} text-[10px] px-2 py-0.5`}>
                            {signal.severity}
                          </Badge>
                          <div className="flex gap-0.5 mt-1">
                            {[...Array(5)].map((_, i) => (
                              <div
                                key={i}
                                className={`w-2 h-2 rounded-full ${i < signal.impact_rating ? 'bg-[#00707C]' : 'bg-[#E0E0E0]'}`}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-[#0D3B4F] font-medium text-sm">{signal.summary}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-[#4E6974]">
                            <span className="bg-white px-2 py-0.5 rounded">
                              {RISK_CATEGORIES_LABELS[signal.signal_type] || signal.signal_type}
                            </span>
                            <span>Source: {signal.source}</span>
                            <span>{formatDate(signal.timestamp)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </motion.div>

          {/* ── Data Enrichment Sections ── */}

          {/* Feature 1 & 2: Sentiment Trend + Media Volume */}
          {(sentimentData?.timeline?.length > 0 || mediaVolumeData?.timeline?.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {sentimentData?.timeline?.length > 0 && (
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid="sentiment-trend-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-bold text-[#0D3B4F]">Sentiment trend (30 days)</CardTitle>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">GDELT</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-[2px] h-16" data-testid="sentiment-sparkline">
                      {sentimentData.timeline.slice(-30).map((d, i) => {
                        const norm = Math.max(0, Math.min(100, (d.sentiment + 8) * 6.25));
                        return (
                          <div key={d.date || i} className="flex-1 rounded-t-sm transition-all" title={`${d.date}: ${d.sentiment}`}
                            style={{ height: `${Math.max(4, norm)}%`, backgroundColor: d.sentiment > 0 ? '#2E7D32' : d.sentiment > -3 ? '#FF9500' : '#FF3B30', opacity: 0.7 + (i / 60) }} />
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between mt-2 text-[10px] text-[#4E6974]">
                      <span>30 days ago</span>
                      <span>Current: {sentimentData.timeline[sentimentData.timeline.length - 1]?.sentiment?.toFixed(1) || '0'}</span>
                      <span>Today</span>
                    </div>
                  </CardContent>
                </Card>
              )}
              {mediaVolumeData?.timeline?.length > 0 && (
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid="media-volume-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-bold text-[#0D3B4F]">Media attention (30 days)</CardTitle>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">GDELT</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-[2px] h-16" data-testid="media-volume-sparkline">
                      {mediaVolumeData.timeline.slice(-30).map((d, i) => {
                        const maxVol = Math.max(...mediaVolumeData.timeline.map(t => t.volume));
                        const pct = maxVol > 0 ? (d.volume / maxVol) * 100 : 50;
                        return (
                          <div key={d.date || i} className="flex-1 rounded-t-sm transition-all" title={`${d.date}: ${d.volume} articles`}
                            style={{ height: `${Math.max(4, pct)}%`, backgroundColor: d.is_spike ? '#FF3B30' : '#0097A7', opacity: 0.5 + (i / 60) }} />
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between mt-2 text-[10px] text-[#4E6974]">
                      <span>30 days ago</span>
                      {mediaVolumeData.timeline.some(d => d.is_spike) && <span className="text-[#FF3B30] font-medium">Spike detected</span>}
                      <span>Today</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Feature 5: Economic Indicators */}
          {econIndicators?.indicators?.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.52 }} className="mt-6">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid="economic-indicators-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-bold text-[#0D3B4F]">Economic indicators</CardTitle>
                    <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">World Bank / IMF</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {econIndicators.indicators.map(ind => (
                      <div key={ind.name} className="p-3 rounded-lg bg-[#F5F5F5]" data-testid={`econ-${ind.name.replace(/\s+/g, '-').toLowerCase()}`}>
                        <span className="text-[10px] text-[#4E6974] uppercase">{ind.name}</span>
                        <div className="text-lg font-bold text-[#0D3B4F] mt-0.5">
                          {typeof ind.value === 'number' ? ind.value.toLocaleString() : ind.value}
                          <span className="text-xs text-[#4E6974] font-normal ml-0.5">{ind.unit}</span>
                        </div>
                        {ind.source && <span className="text-[9px] text-[#4E6974]">{ind.source}</span>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Feature 6: Product-Level Trade */}
          {tradeProducts && (tradeProducts.imports?.length > 0 || tradeProducts.exports?.length > 0) && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.54 }} className="mt-6">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid="trade-products-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-bold text-[#0D3B4F]">Top traded products</CardTitle>
                    <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">UN Comtrade</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {tradeProducts.imports?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-bold text-[#4E6974] uppercase tracking-wide mb-2">Imports from {country.country_name}</h4>
                        <div className="space-y-1">
                          {tradeProducts.imports.slice(0, 5).map((p, i) => (
                            <div key={p.hs_code || i} className="flex items-center justify-between p-2 rounded bg-[#F5F5F5] text-xs">
                              <div className="flex items-center gap-2">
                                <span className="text-[#4E6974] w-4">{i + 1}.</span>
                                <span className="font-medium text-[#0D3B4F]">{p.product}</span>
                                <span className="text-[9px] text-[#4E6974]">HS {p.hs_code}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {p.value_usd_m > 0 && <span className="text-[#0D3B4F] tabular-nums">${p.value_usd_m?.toLocaleString()}M</span>}
                                {p.share_pct > 0 && <span className="text-[#4E6974]">{p.share_pct}%</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {tradeProducts.exports?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-bold text-[#4E6974] uppercase tracking-wide mb-2">Exports to {country.country_name}</h4>
                        <div className="space-y-1">
                          {tradeProducts.exports.slice(0, 5).map((p, i) => (
                            <div key={p.hs_code || i} className="flex items-center justify-between p-2 rounded bg-[#F5F5F5] text-xs">
                              <div className="flex items-center gap-2">
                                <span className="text-[#4E6974] w-4">{i + 1}.</span>
                                <span className="font-medium text-[#0D3B4F]">{p.product}</span>
                                <span className="text-[9px] text-[#4E6974]">HS {p.hs_code}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {p.value_usd_m > 0 && <span className="text-[#0D3B4F] tabular-nums">${p.value_usd_m?.toLocaleString()}M</span>}
                                {p.share_pct > 0 && <span className="text-[#4E6974]">{p.share_pct}%</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Key Ports in this Country */}
          <KeyPortsSection countryCode={country.country_code} countryName={country.country_name} navigate={navigate} />

          {/* Related Reports */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-6">
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-bold text-[#0D3B4F]">Related reports</CardTitle>
                  <Link to="/reports" className="text-[#0097A7] text-sm hover:underline">View all</Link>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div
                    className="p-4 rounded-lg border border-[#E0E0E0] hover:border-[#0097A7] hover:shadow-md transition-all cursor-pointer group"
                    onClick={() => navigate('/reports')}
                    data-testid="related-report-deep-dive"
                  >
                    <FileText className="h-8 w-8 text-[#0097A7] mb-3" />
                    <h4 className="font-semibold text-[#0D3B4F] text-sm mb-1 group-hover:text-[#0097A7] transition-colors">
                      {country.country_name} Deep Dive Risk Assessment
                    </h4>
                    <p className="text-xs text-[#4E6974]">Q2 2026 - Country deep-dive</p>
                  </div>
                  <div
                    className="p-4 rounded-lg border border-[#E0E0E0] hover:border-[#0097A7] hover:shadow-md transition-all cursor-pointer group"
                    onClick={() => navigate('/reports')}
                    data-testid="related-report-weekly"
                  >
                    <FileText className="h-8 w-8 text-[#0097A7] mb-3" />
                    <h4 className="font-semibold text-[#0D3B4F] text-sm mb-1 group-hover:text-[#0097A7] transition-colors">
                      Weekly Risk Summary
                    </h4>
                    <p className="text-xs text-[#4E6974]">Latest - Weekly summary</p>
                  </div>
                  <div
                    className="p-4 rounded-lg border border-[#E0E0E0] hover:border-[#0097A7] hover:shadow-md transition-all cursor-pointer group"
                    onClick={() => navigate('/reports')}
                    data-testid="related-report-sector"
                  >
                    <FileText className="h-8 w-8 text-[#0097A7] mb-3" />
                    <h4 className="font-semibold text-[#0D3B4F] text-sm mb-1 group-hover:text-[#0097A7] transition-colors">
                      Mining and Resources Sector Report
                    </h4>
                    <p className="text-xs text-[#4E6974]">Q2 2026 - Sector report</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

// ─── Disaster Detail Card (Feature 8) ───
const DisasterCard = ({ disaster }) => {
  const [expanded, setExpanded] = useState(false);
  const d = disaster;
  return (
    <div className={`p-2.5 rounded-lg bg-[#FFF3F0] cursor-pointer transition-all ${expanded ? 'ring-1 ring-[#FF9500]/30' : ''}`}
      onClick={() => setExpanded(!expanded)} data-testid={`disaster-card-${d.id || ''}`}>
      <p className="text-sm font-medium text-[#0D3B4F]">{d.name}</p>
      <div className="flex items-center gap-2 mt-1">
        <Badge className={d.severity === 'CRITICAL' ? 'bg-[#FF3B30] text-white text-[9px]' : d.severity === 'HIGH' ? 'bg-[#FF9500] text-white text-[9px]' : 'bg-[#FFD60A] text-[#0D3B4F] text-[9px]'}>
          {d.severity}
        </Badge>
        <span className="text-[10px] text-[#4E6974]">{d.status}</span>
        <span className="text-[10px] text-[#0097A7] ml-auto">{expanded ? 'Less' : 'More'}</span>
      </div>
      {expanded && (
        <div className="mt-2 pt-2 border-t border-[#FFD0C0] text-xs text-[#4E6974] space-y-1">
          {d.description && <p>{d.description}</p>}
          {d.types?.length > 0 && <p>Type: {d.types.join(', ')}</p>}
          {d.magnitude && <p>Magnitude: {d.magnitude}</p>}
          {d.published && <p>Published: {d.published}</p>}
          <p className="text-[9px] italic">Source: GDACS (Global Disaster Alerting Coordination System)</p>
        </div>
      )}
    </div>
  );
};

// ─── Key Ports Section for Country Detail ───
const KeyPortsSection = ({ countryCode, countryName, navigate }) => {
  const [ports, setPorts] = useState([]);
  useEffect(() => {
    api.getCountryPorts(countryCode).then(res => setPorts(res.data?.ports || [])).catch(() => {});
  }, [countryCode]);

  if (ports.length === 0) return null;

  const STATUS_CLR = { Normal: '#2E7D32', Delays: '#FF9500', Congested: '#FF6B00', Disrupted: '#FF3B30' };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.48 }} className="mt-6">
      <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-bold text-[#0D3B4F] flex items-center gap-2">
              <Anchor className="h-5 w-5 text-[#0097A7]" /> Key ports in {countryName}
            </CardTitle>
            <Link to="/ports-routes" className="text-[#0097A7] text-sm hover:underline">View all ports</Link>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="country-key-ports">
            {ports.map(p => (
              <div key={p.port_id} className="p-3 rounded-lg bg-[#F5F5F5] hover:bg-[#EEEEEE] cursor-pointer transition-colors"
                onClick={() => navigate('/ports-routes')} data-testid={`country-port-${p.port_id.toLowerCase()}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-bold text-[#0D3B4F]">{p.port_name}</span>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_CLR[p.status] || '#4E6974' }} />
                    <span className="text-[10px] font-medium" style={{ color: STATUS_CLR[p.status] || '#4E6974' }}>{p.status}</span>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-[#4E6974]">
                  <span>Wait: {p.wait_days}d</span>
                  <span>Dwell: {p.dwell_days}d</span>
                  <span>Util: {p.utilisation_pct}%</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default CountryDetailPage;
